<template>
  <div class="p-4 md:p-8 bg-[#F8FAFC] min-h-screen font-sans text-slate-800 select-none">
    
    <!-- HEADER / NAVIGATION DU POSTE -->
    <header class="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
      <div class="flex flex-col lg:flex-row items-start lg:items-center gap-4 flex-1 w-full">
        <div>
          <h1 class="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
            <span>{{ t.station }}</span>
            <span :class="selectedQueueId ? 'text-amber-600' : 'text-blue-600'">
              {{ selectedQueueId ? t.temporaryMode : (counterName || t.assignedCounter) }}
            </span>
          </h1>
          <p class="text-xs text-slate-500 font-semibold mt-0.5">{{ t.agent }} {{ userData.firstName }} {{ userData.lastName }}</p>
        </div>

        <!-- Sélecteur de file d'attente (Dropdown) -->
        <div class="relative w-full lg:w-auto">
          <button
            @click="showQueueSelector = !showQueueSelector"
            class="w-full lg:w-auto flex items-center justify-between gap-4 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl hover:border-blue-500 transition-all shadow-sm cursor-pointer"
          >
            <div class="text-left">
              <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest">{{ t.currentStation }}</p>
              <p class="text-xs font-bold text-slate-800 truncate max-w-[180px]">
                {{ selectedQueueId ? currentQueueName : (counterName || t.loading) }}
              </p>
            </div>
            <div class="flex items-center gap-2">
              <span v-if="isTemporaryMode" class="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 text-[9px] font-black uppercase rounded-md">
                {{ t.temporary }}
              </span>
              <span v-else class="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 text-[9px] font-black uppercase rounded-md">
                {{ t.fixed }}
              </span>
              <svg class="w-4 h-4 text-slate-400 transition-transform" :class="{'rotate-180': showQueueSelector}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-width="2.5" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          <!-- Dropdown Menu -->
          <div v-if="showQueueSelector" class="absolute left-0 lg:left-auto top-full mt-2 w-full lg:w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-[100] overflow-hidden">
            <div class="p-2 border-b border-slate-100 bg-slate-50">
              <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2">{{ t.chooseQueue }}</p>
            </div>

            <div class="max-h-60 overflow-y-auto">
              <button
                @click="resetToAssignedMode"
                class="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex justify-between items-center border-b border-slate-100 cursor-pointer"
              >
                <span class="text-xs font-bold text-blue-600">{{ t.autoCounterMode }}</span>
                <svg v-if="!selectedQueueId" class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>
              </button>

              <button 
                v-for="queue in availableQueues" 
                :key="queue._id"
                @click="selectQueue(queue._id)"
                class="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-none flex justify-between items-center cursor-pointer"
              >
                <div>
                  <p class="text-xs font-bold text-slate-800">{{ queue.name }}</p>
                  <p class="text-[10px] text-slate-400 font-semibold">{{ queue.waitingCount }} {{ t.waitingClients }}</p>
                </div>
                <svg v-if="selectedQueueId === queue._id" class="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Statut Connexion Temps Réel -->
      <div class="flex items-center gap-4 w-full lg:w-auto justify-between lg:justify-end border-t lg:border-t-0 pt-3 lg:pt-0 border-slate-100">
        <div class="text-right hidden sm:block">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ t.agencyStatus }}</p>
          <p class="text-xs font-bold text-emerald-600 flex items-center gap-1.5 justify-end">
            <span class="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            {{ socketConnected ? t.realtimeActive : t.reconnecting }}
          </p>
        </div>
        <div class="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center font-black text-sm shadow-sm">
          {{ userData.firstName ? userData.firstName[0] : '?' }}
        </div>
      </div>
    </header>

    <!-- GRILLE PRINCIPALE (POS Layout) -->
    <div class="grid xl:grid-cols-3 gap-6">
      
      <!-- COLONNE GAUCHE & CENTRE (2/3) : CONTRÔLE DU TICKET ACTIF -->
      <div class="xl:col-span-2 space-y-6">
        
        <!-- File vide -->
        <div v-if="waitingTickets.length === 0 && !currentTicket"
             class="bg-white border border-slate-200/80 rounded-2xl p-10 flex flex-col items-center text-center shadow-sm">
          <div class="w-14 h-14 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 mb-3 shadow-sm">
            <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 class="text-base font-black text-slate-900 uppercase">{{ t.emptyQueue }}</h2>
          <p class="text-slate-500 text-xs font-medium mt-1">{{ t.allClientsServed }}</p>
        </div>

        <!-- Interface de Caisse / POS actif -->
        <div v-else class="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-slate-200/80 relative overflow-hidden">
          
          <!-- Badge mode temporaire -->
          <div v-if="isTemporaryMode" class="absolute top-6 right-6 px-3 py-1 bg-amber-50 border border-amber-200 rounded-lg">
            <p class="text-[10px] font-black text-amber-700 uppercase tracking-wider">{{ t.temporaryMode }}</p>
          </div>

          <div class="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
             <div class="flex flex-col">
                <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ t.inProgress }}</span>
                <h2 class="text-sm font-extrabold text-slate-800 uppercase">{{ currentTicket?.queueId?.name || t.service }}</h2>
             </div>
             <span class="px-3 py-1 bg-slate-900 text-white rounded-lg text-xs font-black tracking-wider uppercase">
               {{ t.ticket }} #{{ currentTicket?._id ? currentTicket._id.slice(-4) : '0000' }}
             </span>
          </div>

          <!-- Écran de Terminal d'Appel (Style POS sombre et lisible) -->
          <div class="flex flex-col items-center justify-center py-12 bg-[#0B0F19] rounded-2xl mb-6 shadow-inner text-white">
            <p class="text-[10px] font-black text-blue-400 uppercase tracking-[0.3em] mb-2">{{ t.callNumber }}</p>
            <div class="text-8xl md:text-9xl font-black tracking-tighter tabular-nums text-white">
              {{ currentTicket?.number || '---' }}
            </div>
            
            <div class="mt-4 flex flex-col items-center gap-2">
               <span class="px-3 py-1 bg-white/10 rounded-lg text-[10px] font-bold text-slate-300 uppercase tracking-widest">
                 {{ currentTicket?.status === 'CALLED' ? t.called : t.waiting }}
               </span>

               <!-- Countdown Timer pour auto-finish -->
               <div v-if="currentTicket?.status === 'CALLED' && currentTicket?.autoFinishAt" class="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-xl mt-1">
                 <svg class="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                 </svg>
                 <span class="text-[11px] font-bold text-amber-300">
                   {{ t.autoFinishIn }}: <span class="tabular-nums text-white">{{ remainingTimeFormatted }}</span>
                 </span>
                 <div class="w-12 h-1 bg-amber-900/50 rounded-full overflow-hidden ml-1">
                   <div class="h-full bg-amber-400 transition-all duration-1000" :style="{ width: progressPercent + '%' }"></div>
                 </div>
               </div>
            </div>
          </div>

          <!-- Boutons d'actions rapides style POS (Tactiles & Réactifs) -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <button @click="callAgain" class="flex flex-col items-center justify-center p-4 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200/80 transition-all group cursor-pointer">
              <svg class="w-5 h-5 mb-1 text-slate-400 group-hover:text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5 9v6h4l5 5V4L9 9H5z" /></svg>
              <span class="text-[10px] font-black uppercase tracking-wider text-slate-700">{{ t.recall }}</span>
            </button>

            <button @click="processNext" :disabled="loading" class="flex flex-col items-center justify-center p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md shadow-blue-500/20 transition-all disabled:opacity-50 cursor-pointer">
              <svg v-if="!loading" class="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
              <span v-else class="animate-spin rounded-full h-6 w-6 border-2 border-white mb-1"></span>
              <span class="text-xs font-black uppercase tracking-wider">{{ t.next }}</span>
            </button>

            <button @click="markAbsent" class="flex flex-col items-center justify-center p-4 bg-slate-50 hover:bg-rose-50 rounded-xl border border-slate-200/80 group transition-all cursor-pointer">
              <svg class="w-5 h-5 mb-1 text-slate-400 group-hover:text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              <span class="text-[10px] font-black uppercase tracking-wider text-slate-700 group-hover:text-rose-600">{{ t.absent }}</span>
            </button>
          </div>

          <!-- Actions secondaires : Transférer et Fin de service -->
          <div class="grid sm:grid-cols-2 gap-3">
            <button @click="showTransferModal = true" :disabled="!currentTicket" class="flex items-center justify-center gap-2 p-3.5 bg-amber-50 hover:bg-amber-100 rounded-xl border border-amber-200/60 transition-all group disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
              <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span class="text-xs font-black uppercase tracking-wider text-amber-700">{{ t.transfer }}</span>
            </button>

            <button @click="finishService" :disabled="!currentTicket" class="flex items-center justify-center gap-2 p-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md shadow-emerald-500/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-width="2.5" d="M5 13l4 4L19 7" />
              </svg>
              <span class="text-xs font-black uppercase tracking-wider">{{ t.endService }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- COLONNE DROITE (1/3) : PROCHAINS CLIENTS & HISTORIQUE -->
      <div class="space-y-6">
        
        <!-- Prochains Clients -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xs font-black uppercase tracking-widest text-slate-400">{{ t.nextClients }}</h3>
            <span class="bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded-md">{{ waitingTickets.length }}</span>
          </div>
          
          <div class="space-y-2 max-h-[350px] overflow-y-auto pr-1">
            <div v-for="ticket in waitingTickets" :key="ticket._id"
                 class="p-3 rounded-xl border border-slate-200/60 bg-slate-50/50 flex justify-between items-center group hover:bg-white hover:border-blue-300 transition-all">
              <div>
                <p class="text-xs font-black text-slate-900">{{ ticket.number }}</p>
                <p class="text-[9px] font-bold text-slate-400 uppercase">{{ ticket.queueId?.name || 'Accueil' }}</p>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-[10px] font-extrabold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                  {{ getWaitTime(ticket.createdAt) }} min
                </span>
                <button
                  @click="callSpecificTicket(ticket)"
                  :disabled="!socketConnected"
                  class="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-lg transition-all disabled:opacity-40 cursor-pointer shadow-sm"
                  title="Appeler ce ticket"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5 9v6h4l5 5V4L9 9H5z" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div v-if="waitingTickets.length === 0" class="text-center py-10 text-slate-300 font-bold uppercase text-[10px]">
              {{ t.noTicket }}
            </div>
          </div>
        </div>

        <!-- Section Historique -->
        <div class="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xs font-black uppercase tracking-widest text-slate-400">{{ t.history }}</h3>
            <span class="bg-emerald-600 text-white text-[10px] font-black px-2 py-0.5 rounded-md">{{ completedTickets.length }}</span>
          </div>
          
          <div class="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            <div v-for="ticket in completedTickets" :key="ticket._id"
                 class="p-3 rounded-xl border border-slate-200/60 bg-slate-50/50 flex justify-between items-center group hover:bg-white transition-all">
              <div>
                <p class="text-xs font-black text-slate-900">{{ ticket.number }}</p>
                <p class="text-[9px] font-bold text-slate-400 uppercase">{{ ticket.queueId?.name || 'Accueil' }}</p>
              </div>
              <button
                @click="recallFromHistory(ticket)"
                :disabled="!socketConnected"
                class="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 p-1.5 rounded-lg transition-all disabled:opacity-40 cursor-pointer"
                :title="t.recallFromHistory"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            
            <div v-if="completedTickets.length === 0" class="text-center py-8 text-slate-300 font-bold uppercase text-[10px]">
              {{ t.noHistory }}
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ✅ Modal de transfert de ticket -->
    <div
      v-if="showTransferModal"
      class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      @click.self="showTransferModal = false"
    >
      <div class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-200">
        <div class="text-center mb-5">
          <h2 class="text-base font-black text-slate-900 uppercase mb-1">{{ t.transferTicket }}</h2>
          <p class="text-xs text-amber-600 font-bold">{{ t.ticket }} {{ currentTicket?.number }} - {{ currentTicket?.queueId?.name }}</p>
        </div>

        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5">{{ t.selectDestinationQueue }}</p>

          <div v-if="availableQueues.length === 0" class="text-center py-6 text-slate-400 text-xs font-medium">
            {{ t.noAvailableQueues }}
          </div>

          <div class="space-y-2 max-h-48 overflow-y-auto pr-1">
            <button
              v-for="queue in availableQueues"
              :key="queue._id"
              @click="selectedTransferQueue = queue._id"
              :disabled="queue._id === currentTicket?.queueId?._id"
              class="w-full text-left px-3 py-2.5 rounded-lg transition-all border text-xs disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              :class="selectedTransferQueue === queue._id ? 'bg-amber-50 border-amber-500 font-bold text-amber-800' : 'bg-white border-slate-200 text-slate-700'"
            >
              <div class="flex justify-between items-center">
                <span>{{ queue.name }}</span>
                <span class="text-[10px] text-slate-400 font-semibold">{{ queue.waitingCount || 0 }} en attente</span>
              </div>
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2.5">
          <button
            @click="showTransferModal = false; selectedTransferQueue = null"
            class="px-4 py-3 bg-slate-100 rounded-xl font-bold uppercase text-xs text-slate-600 hover:bg-slate-200 transition-all cursor-pointer"
          >
            {{ t.cancel }}
          </button>
          <button
            @click="transferTicketToQueue"
            :disabled="!selectedTransferQueue || selectedTransferQueue === currentTicket?.queueId?._id"
            class="px-4 py-3 bg-amber-600 text-white rounded-xl font-bold uppercase text-xs hover:bg-amber-700 transition-all shadow-md shadow-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            {{ t.transfer }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de suggestion de switch automatique -->
    <div
      v-if="showSwitchSuggestion && suggestedQueue"
      class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      @click.self="declineSwitch"
    >
      <div class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-200 text-center">
        <h2 class="text-base font-black text-slate-900 uppercase mb-1">{{ t.emptyQueue }}</h2>
        <p class="text-xs text-slate-500 font-medium mb-4">{{ t.noMoreTickets }}</p>

        <div class="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-5">
          <p class="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">{{ t.switchSuggestion }}</p>
          <p class="text-sm font-black text-slate-900">{{ suggestedQueue.name }}</p>
          <p class="text-xs text-slate-500 mt-1 font-semibold">{{ suggestedQueue.waitingCount }} {{ t.waitingClients }}</p>
        </div>

        <div class="grid grid-cols-2 gap-2.5">
          <button
            @click="declineSwitch"
            class="px-4 py-3 bg-slate-100 rounded-xl font-bold uppercase text-xs text-slate-600 hover:bg-slate-200 transition-all cursor-pointer"
          >
            {{ t.stayHere }}
          </button>
          <button
            @click="acceptSwitch"
            class="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold uppercase text-xs hover:bg-blue-700 transition-all shadow-md shadow-blue-500/20 cursor-pointer"
          >
            {{ t.switchQueue }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import { io } from "socket.io-client";
import { sendNotification } from '@/utils/notificationSettings';

export default {
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
      tickets: [],
      historyTickets: [], // ✅ Tickets terminés pour l'historique
      loading: false,
      selectedQueueId: null,
      socket: null,
      socketConnected: false,
      availableQueues: [], // Toutes les files disponibles avec leur charge
      showQueueSelector: false, // Toggle du dropdown
      showSwitchSuggestion: false, // Modal de suggestion de switch
      suggestedQueue: null, // File suggérée pour le switch
      statsRefreshInterval: null, // Interval pour rafraîchir les stats
      counterName: "",
      showTransferModal: false, // ✅ Modal de transfert
      selectedTransferCounter: null, // ✅ Guichet sélectionné pour le transfert (ancien système)
      selectedTransferQueue: null, // ✅ File sélectionnée pour le transfert inter-files
      availableCounters: [], // ✅ Liste des guichets disponibles (ancien système)
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      recallingTicketId: null, // ✅ ID du ticket en cours de rappel
      currentTime: Date.now(), // ✅ Temps actuel pour le countdown
      countdownTimer: null, // ✅ Timer pour mettre à jour le countdown
      translations: {
        fr: {
          station: "Poste :",
          temporaryMode: "Mode Temporaire",
          assignedCounter: "Guichet Assigné",
          agent: "Agent :",
          currentStation: "Poste Actuel",
          loading: "Chargement...",
          temporary: "Temporaire",
          fixed: "Fixe",
          chooseQueue: "Choisir une file",
          autoCounterMode: "Mode Guichet (Auto)",
          waitingClients: "clients en attente",
          agencyStatus: "Statut Agence",
          realtimeActive: "Temps Réel Actif",
          reconnecting: "Reconnexion...",
          emptyQueue: "File d'attente vide",
          allClientsServed: "Tous les clients ont été servis.",
          inProgress: "En cours de traitement",
          service: "Service",
          ticket: "Ticket",
          callNumber: "Numéro d'appel",
          called: "Appelé",
          waiting: "En attente",
          recall: "Rappeler",
          next: "Suivant",
          absent: "Absent",
          transfer: "Transférer",
          endService: "Fin de service",
          nextClients: "Prochains clients",
          noTicket: "Aucun ticket",
          transferTicket: "Transférer le ticket",
          selectDestination: "Sélectionner le guichet de destination",
          selectDestinationQueue: "Sélectionner la file de destination",
          currentQueue: "File Actuelle",
          noAvailableQueues: "Aucune file disponible",
          busy: "Occupé",
          available: "Disponible",
          cancel: "Annuler",
          noMoreTickets: "Votre file d'attente actuelle n'a plus de tickets en attente.",
          switchSuggestion: "Suggestion de switch",
          calledPlural: "appelé",
          stayHere: "Rester ici",
          switchQueue: "Switcher",
          history: "Historique",
          completed: "Terminé",
          recallFromHistory: "Rappeler depuis l'historique",
          noHistory: "Aucun historique",
          autoFinishIn: "Fin auto dans"
        },
        en: {
          station: "Station:",
          temporaryMode: "Temporary Mode",
          assignedCounter: "Assigned Counter",
          agent: "Agent:",
          currentStation: "Current Station",
          loading: "Loading...",
          temporary: "Temporary",
          fixed: "Fixed",
          chooseQueue: "Choose a queue",
          autoCounterMode: "Counter Mode (Auto)",
          waitingClients: "waiting clients",
          agencyStatus: "Agency Status",
          realtimeActive: "Realtime Active",
          reconnecting: "Reconnecting...",
          emptyQueue: "Empty queue",
          allClientsServed: "All clients have been served.",
          inProgress: "In Progress",
          service: "Service",
          ticket: "Ticket",
          callNumber: "Call Number",
          called: "Called",
          waiting: "Waiting",
          recall: "Recall",
          next: "Next",
          absent: "Absent",
          transfer: "Transfer",
          endService: "End Service",
          nextClients: "Next Clients",
          noTicket: "No ticket",
          transferTicket: "Transfer ticket",
          selectDestination: "Select destination counter",
          selectDestinationQueue: "Select destination queue",
          currentQueue: "Current Queue",
          noAvailableQueues: "No available queues",
          busy: "Busy",
          available: "Available",
          cancel: "Cancel",
          noMoreTickets: "Your current queue has no more waiting tickets.",
          switchSuggestion: "Switch Suggestion",
          calledPlural: "called",
          stayHere: "Stay here",
          switchQueue: "Switch",
          history: "History",
          completed: "Completed",
          recallFromHistory: "Recall from history",
          noHistory: "No history",
          autoFinishIn: "Auto-finish in"
        },
        de: {
          station: "Station:",
          temporaryMode: "Temporärer Modus",
          assignedCounter: "Zugewiesener Schalter",
          agent: "Agent:",
          currentStation: "Aktuelle Station",
          loading: "Wird geladen...",
          temporary: "Temporär",
          fixed: "Fest",
          chooseQueue: "Warteschlange wählen",
          autoCounterMode: "Schaltermodus (Auto)",
          waitingClients: "wartende Kunden",
          agencyStatus: "Agenturstatus",
          realtimeActive: "Echtzeit Aktiv",
          reconnecting: "Wiederverbinden...",
          emptyQueue: "Leere Warteschlange",
          allClientsServed: "Alle Kunden wurden bedient.",
          inProgress: "In Bearbeitung",
          service: "Service",
          ticket: "Ticket",
          callNumber: "Rufnummer",
          called: "Aufgerufen",
          waiting: "Wartend",
          recall: "Erneut rufen",
          next: "Nächster",
          absent: "Abwesend",
          transfer: "Übertragen",
          endService: "Service beenden",
          nextClients: "Nächste Kunden",
          noTicket: "Kein Ticket",
          transferTicket: "Ticket übertragen",
          selectDestination: "Zielschalter auswählen",
          selectDestinationQueue: "Zielwarteschlange auswählen",
          currentQueue: "Aktuelle Warteschlange",
          noAvailableQueues: "Keine Warteschlangen verfügbar",
          busy: "Besetzt",
          available: "Verfügbar",
          cancel: "Abbrechen",
          noMoreTickets: "Ihre aktuelle Warteschlange hat keine wartenden Tickets mehr.",
          switchSuggestion: "Wechselvorschlag",
          calledPlural: "aufgerufen",
          stayHere: "Hier bleiben",
          switchQueue: "Wechseln",
          history: "Verlauf",
          completed: "Abgeschlossen",
          recallFromHistory: "Aus Verlauf erneut rufen",
          noHistory: "Kein Verlauf",
          autoFinishIn: "Auto-Ende in"
        },
        it: {
          station: "Postazione:",
          temporaryMode: "Modalità Temporanea",
          assignedCounter: "Sportello Assegnato",
          agent: "Agente:",
          currentStation: "Postazione Attuale",
          loading: "Caricamento...",
          temporary: "Temporaneo",
          fixed: "Fisso",
          chooseQueue: "Scegli una coda",
          autoCounterMode: "Modalità Sportello (Auto)",
          waitingClients: "clienti in attesa",
          agencyStatus: "Stato Agenzia",
          realtimeActive: "Tempo Reale Attivo",
          reconnecting: "Riconnessione...",
          emptyQueue: "Coda vuota",
          allClientsServed: "Tutti i clienti sono stati serviti.",
          inProgress: "In Corso",
          service: "Servizio",
          ticket: "Ticket",
          callNumber: "Numero di chiamata",
          called: "Chiamato",
          waiting: "In attesa",
          recall: "Richiamare",
          next: "Successivo",
          absent: "Assente",
          transfer: "Trasferisci",
          endService: "Fine servizio",
          nextClients: "Prossimi Clienti",
          noTicket: "Nessun ticket",
          transferTicket: "Trasferisci ticket",
          selectDestination: "Seleziona sportello di destinazione",
          selectDestinationQueue: "Seleziona coda di destinazione",
          currentQueue: "Coda Attuale",
          noAvailableQueues: "Nessuna coda disponibile",
          busy: "Occupato",
          available: "Disponibile",
          cancel: "Annulla",
          noMoreTickets: "La tua coda attuale non ha più ticket in attesa.",
          switchSuggestion: "Suggerimento di cambio",
          calledPlural: "chiamato",
          stayHere: "Resta qui",
          switchQueue: "Cambia",
          history: "Storico",
          completed: "Completato",
          recallFromHistory: "Richiama dallo storico",
          noHistory: "Nessuno storico",
          autoFinishIn: "Fine auto tra"
        }
      },
     
     
    }
  },
  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    },

    // Temps restant avant auto-finish (en secondes)
    remainingTimeSeconds() {
      if (!this.currentTicket?.autoFinishAt) return 0;
      const finishTime = new Date(this.currentTicket.autoFinishAt).getTime();
      const diff = finishTime - this.currentTime;
      return Math.max(0, Math.floor(diff / 1000));
    },

    // Temps restant formaté (MM:SS)
    remainingTimeFormatted() {
      const totalSeconds = this.remainingTimeSeconds;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },

    // Pourcentage de progression (0-100)
    progressPercent() {
      if (!this.currentTicket?.autoFinishAt || !this.currentTicket?.calledAt) return 0;
      const startTime = new Date(this.currentTicket.calledAt).getTime();
      const finishTime = new Date(this.currentTicket.autoFinishAt).getTime();
      const totalDuration = finishTime - startTime;
      const elapsed = this.currentTime - startTime;
      return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
    },

waitingTickets() {
  if (!this.tickets || !Array.isArray(this.tickets)) return [];

  return this.tickets.filter(t => {
    const isWaiting = t.status && t.status.toUpperCase() === "WAITING";

    // Si on a sélectionné une file précise, on ne garde que celle-là
    if (this.selectedQueueId) {
      return isWaiting && t.queueId?._id === this.selectedQueueId;
    }

    // Sinon, on ne devrait afficher que les tickets dont la file est
    // censée être gérée par ce guichet (Logique à adapter selon votre DB)
    return isWaiting;
  });
},
currentTicket() {
  if (!this.tickets || !Array.isArray(this.tickets)) return null;

  // On ne récupère le ticket "CALLED" que s'il appartient à MON guichet
  return this.tickets.find(t =>
    t.status &&
    t.status.toUpperCase() === "CALLED" &&
    t.counterId === this.userData.assignedCounterId
  );
},
completedTickets() {
  if (!this.historyTickets || !Array.isArray(this.historyTickets)) return [];

  // Use the spread operator [...] to create a new array instance
  return [...this.historyTickets].sort((a, b) => {
    return new Date(b.updatedAt) - new Date(a.updatedAt);
  });
},
    isTemporaryMode() {
      // Mode temporaire si une file est sélectionnée manuellement ET que ce n'est pas celle du guichet assigné
      return this.selectedQueueId !== null;
    },
    currentQueueName() {
      if (this.selectedQueueId) {
        const queue = this.availableQueues.find(q => q._id === this.selectedQueueId);
        return queue ? queue.name : "File inconnue";
      }
      return "Guichet assigné";
    }
  },
  methods: {
    // 1. Initialisation de la connexion Socket
    initSocket() {
      const token = localStorage.getItem("token") || localStorage.getItem("userToken");
      console.log('🔌 Init WebSocket avec token:', token ? 'Present' : 'Missing');

      if (!token) {
        console.error('❌ Token manquant - impossible de se connecter au WebSocket');
        toast.error('Erreur: Token manquant. Veuillez vous reconnecter.');
        return;
      }

      // Backend local
      this.socket = io("https://api.datafuse.fr", {
        query: { token: token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });

      this.socket.on("connect", () => {
        this.socketConnected = true;
        console.log("✅ WebSocket connecté - ID:", this.socket.id);
        toast.success('Connecté au serveur');
      });

      this.socket.on("connect_error", (error) => {
        console.error("❌ WS CONNECTION ERROR:", error.message);
        this.socketConnected = false;
        toast.error('Erreur de connexion WebSocket');
      });

      this.socket.on("error", (error) => {
        console.error("❌ WS ERROR:", error);
        toast.error('Erreur WebSocket');
      });

      // Écouter quand un nouveau ticket est créé par une borne
      this.socket.on("queue_updated", (data) => {
        console.log("♻️ Mise à jour de la file reçue", data);

        // Utiliser le nouveau système de notifications (vérifie automatiquement les paramètres)
        sendNotification("queue_updated", {
          icon: "📊",
          title: "File mise à jour",
          message: `${data.waitingCount || 0} clients en attente`,
          notifType: "info"
        });

        // Ne pas refresh si on est en train de rappeler notre propre ticket actuel
        const isRecallingCurrent = this.recallingTicketId && this.currentTicket && this.recallingTicketId === this.currentTicket._id;
        if (!isRecallingCurrent) {
          this.fetchQueue();
        } else {
          console.log("⏸️ Rappel en cours, skip fetchQueue()");
        }
        this.fetchAvailableQueues(); // Rafraîchir aussi les stats
      });

      // Écouter quand un ticket est appelé (par soi-même ou un collègue)
      this.socket.on("ticket_called", (data) => {
        // Utiliser le nouveau système de notifications
        sendNotification("ticket_called", {
          icon: "📞",
          title: "Ticket appelé",
          message: `${data.ticket?.number || 'Ticket'} au ${data.counterName || 'guichet'}`,
          notifType: "success"
        });

        // Ne pas refresh si on est en train de rappeler notre propre ticket actuel
        const isRecallingCurrent = this.recallingTicketId && this.currentTicket && this.recallingTicketId === this.currentTicket._id;
        if (!isRecallingCurrent) {
          this.fetchQueue();
        } else {
          console.log("⏸️ Rappel en cours, skip fetchQueue()");
        }
        this.fetchAvailableQueues(); // Rafraîchir aussi les stats
        if (data.isRecall) {
          toast.info(`Ticket ${data.ticket.number} rappelé`);
        }
      });

      // Écouter quand un ticket est terminé
      this.socket.on("ticket_finished", (data) => {
        console.log("✅ Événement ticket_finished reçu:", data);
        this.fetchQueue();
        this.fetchHistory(); // ✅ Recharger l'historique quand un ticket est terminé
        this.fetchAvailableQueues();
      });

      this.socket.on("disconnect", (reason) => {
        this.socketConnected = false;
        console.log("❌ WebSocket déconnecté:", reason);
        toast.warning('Déconnecté du serveur');
      });

      this.socket.on("reconnect", (attemptNumber) => {
        console.log("🔄 WebSocket reconnecté après", attemptNumber, "tentatives");
        this.socketConnected = true;
        toast.success('Reconnecté au serveur');
      });
    },

    // 2. Récupérer toutes les files disponibles
    async fetchAvailableQueues() {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("userToken");
        const res = await fetch("https://api.datafuse.fr/api/agency/available-queues", {
          headers: { "Authorization": `Bearer ${token}` }
        });

        const data = await res.json();
        if (res.ok && data.queues) {
          this.availableQueues = data.queues;
          console.log(`📋 ${data.queues.length} files disponibles chargées`);
        }
      } catch (err) {
        console.error("❌ Erreur chargement files disponibles:", err);
      }
    },

    // 3. Récupérer l'historique des tickets terminés
    async fetchHistory() {
      console.log("📚 Chargement de l'historique des tickets terminés...");
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("userToken");

        if (!token) {
          console.error("❌ Token absent du localStorage");
          return;
        }

        const url = "https://api.datafuse.fr/api/agency/history";

        const res = await fetch(url, {
          headers: { "Authorization": `Bearer ${token}` }
        });

        const data = await res.json();

        if (res.ok) {
          if (data.history && Array.isArray(data.history)) {
            this.historyTickets = data.history;
            console.log(`✅ ${data.history.length} tickets terminés chargés dans l'historique`);
          } else {
            console.warn("⚠️ Aucun historique retourné par le backend");
            this.historyTickets = [];
          }
        } else {
          console.error("❌ Erreur API History:", data.error || data.message);
          this.historyTickets = [];
        }
      } catch (err) {
        console.error("❌ Erreur critique fetchHistory:", err);
        this.historyTickets = [];
      }
    },

    // 4. Fetch initial
async fetchQueue() {
  console.log("🚀 Lancement fetchQueue...");
  try {
    const token = localStorage.getItem("token") || localStorage.getItem("userToken");
    
    if (!token) {
      console.error("❌ Token absent du localStorage");
      return;
    }

    let url = "https://api.datafuse.fr/api/agency/queues";
    if (this.selectedQueueId) {
      url += `?queueId=${this.selectedQueueId}`;
      console.log(`🔍 Mode manuel : Filtrage sur la file unique ID: ${this.selectedQueueId}`);
    }

    const res = await fetch(url, { 
      headers: { "Authorization": `Bearer ${token}` } 
    });
    
    const data = await res.json();
    console.log ("DATA: ", data)
    if (res.ok) {
      // --- LOG DU GUICHET ASSIGNÉ ---
      if (data.counter) {
        console.group("🏢 INFOS GUICHET");
        console.log(`Nom : ${data.counter.name}`);
        console.log(`ID Guichet : ${data.counter._id}`);
        // Affiche les noms des services que ce guichet est censé traiter
        const serviceNames = data.counter.serviceIds?.map(s => s.name).join(", ");
        console.log(`Files (Services) assignées au guichet : [ ${serviceNames} ]`);
        console.groupEnd();

        this.counterName = data.counter.name;
        this.priorities = data.counter.serviceIds.map(s => s._id || s);
      } else {
        console.warn("⚠️ Attention : Aucun guichet n'est retourné par le backend pour cet utilisateur.");
      }

      // --- LOG DES TICKETS ET LEURS FILES ---
      if (data.queue) {
        console.log(`📥 Total tickets reçus : ${data.queue.length}`);

        // Compter par statut
        const statusCount = {};
        data.queue.forEach(ticket => {
          const status = ticket.status || "UNKNOWN";
          statusCount[status] = (statusCount[status] || 0) + 1;
        });
        console.log("📊 Répartition par statut:", statusCount);

        data.queue.forEach((ticket, index) => {
          // On affiche le préfixe/nom de la file pour chaque ticket
          const queueName = ticket.queueId?.name || "Inconnue";
          const queuePrefix = ticket.queueId?.prefix || "?";
          console.log(`   Ticket #${index + 1} [${queuePrefix}${ticket.number}] -> File: ${queueName} | Status: ${ticket.status} | CounterId: ${ticket.counterId}`);
        });

        let tickets = data.queue;

        // Tri local
        tickets.sort((a, b) => {
          const isMyTicketA = a.status === 'CALLED' && a.counterId === this.userData.assignedCounterId;
          const isMyTicketB = b.status === 'CALLED' && b.counterId === this.userData.assignedCounterId;
          if (isMyTicketA) return -1;
          if (isMyTicketB) return 1;
          return new Date(a.createdAt) - new Date(b.createdAt);
        });

        this.tickets = tickets;
      }
    } else {
      console.error("❌ Erreur API Queues:", data.error);
    }

    this.$nextTick(() => { 
      if (typeof this.checkAutoSwitch === 'function') {
        this.checkAutoSwitch(); 
      }
    });

  } catch (err) {
    console.error("❌ Erreur critique fetchQueue:", err);
  }
},
    // 3. Action : SUIVANT
    processNext() {
      if (this.waitingTickets.length === 0 && !this.currentTicket) {
        toast.info("Plus personne en attente");
        return;
      }

      this.loading = true;

      // Construction des paramètres pour call_next
      const callParams = {
        counterId: this.userData.assignedCounterId,
        counterName: `${this.userData.firstName}`
      };

      // Si mode temporaire, ajouter le queueId sélectionné
      if (this.selectedQueueId) {
        callParams.queueId = this.selectedQueueId;
        console.log(`🔄 Appel suivant en mode temporaire pour file ${this.selectedQueueId}`);
      }

      // On émet l'événement socket défini dans handleConnection.js
      this.socket.emit("call_next", callParams);

      // On laisse le socket mettre à jour la vue via fetchQueue()
      setTimeout(() => { this.loading = false; }, 800);
    },

    // 4. Action : RAPPELER
    callAgain() {
      if (!this.currentTicket) return;

      // Sauvegarder l'ID du ticket qu'on rappelle
      this.recallingTicketId = this.currentTicket._id;

      this.socket.emit("recall_ticket", {
        ticketId: this.currentTicket._id,
        counterId: this.userData.assignedCounterId,
        counterName: `${this.userData.firstName}`
      });

      // Réinitialiser après un délai raisonnable
      setTimeout(() => {
        this.recallingTicketId = null;
      }, 2000);

      toast.success("Rappel envoyé à l'écran");
    },

    // 5. Action : ABSENT
    markAbsent() {
      if (!this.currentTicket) return;

      this.socket.emit("mark_absent", {
        ticketId: this.currentTicket._id
      });
      toast.warning("Client marqué absent");
    },

    // 6. Action : APPELER UN TICKET SPÉCIFIQUE
    callSpecificTicket(ticket) {
      if (!ticket) {
        console.error('❌ Ticket manquant');
        return;
      }

      if (!this.socketConnected) {
        toast.error('WebSocket non connecté');
        return;
      }

      if (!this.userData.assignedCounterId) {
        toast.error('Aucun guichet assigné');
        return;
      }

      console.log('📞 Appel direct du ticket:', {
        ticketId: ticket._id,
        number: ticket.number,
        counterId: this.userData.assignedCounterId,
        counterName: this.counterName || this.userData.firstName,
        agencyId: this.userData.agencyId
      });

      this.socket.emit('call_specific_ticket', {
        ticketId: ticket._id,
        counterId: this.userData.assignedCounterId,
        counterName: this.counterName || this.userData.firstName,
        agencyId: this.userData.agencyId
      });

      toast.success(`📞 Ticket ${ticket.number} appelé au ${this.counterName || this.userData.firstName}`);
    },

    // ✅ 7. Action : TRANSFÉRER LE TICKET
    async transferTicket() {
      if (!this.currentTicket || !this.selectedTransferCounter) {
        toast.error('Sélectionnez un guichet de destination');
        return;
      }

      const targetCounter = this.availableCounters.find(c => c._id === this.selectedTransferCounter);
      if (!targetCounter) {
        toast.error('Guichet non trouvé');
        return;
      }

      console.log('📤 Transfert du ticket:', {
        ticketId: this.currentTicket._id,
        fromCounter: this.userData.assignedCounterId,
        toCounter: this.selectedTransferCounter,
        agencyId: this.userData.agencyId
      });

      this.socket.emit('transfer_ticket', {
        ticketId: this.currentTicket._id,
        fromCounterId: this.userData.assignedCounterId,
        toCounterId: this.selectedTransferCounter,
        toCounterName: targetCounter.name,
        agencyId: this.userData.agencyId
      });

      toast.success(`Ticket ${this.currentTicket.number} transféré vers ${targetCounter.name}`);
      this.showTransferModal = false;
      this.selectedTransferCounter = null;
    },

    // ✅ 7b. Action : TRANSFÉRER LE TICKET VERS UNE AUTRE FILE
    async transferTicketToQueue() {
      if (!this.currentTicket || !this.selectedTransferQueue) {
        toast.error('Sélectionnez une file de destination');
        return;
      }

      // Ne pas permettre de transférer vers la même file
      if (this.selectedTransferQueue === this.currentTicket.queueId?._id) {
        toast.error('Le ticket est déjà dans cette file');
        return;
      }

      const targetQueue = this.availableQueues.find(q => q._id === this.selectedTransferQueue);
      if (!targetQueue) {
        toast.error('File non trouvée');
        return;
      }

      console.log('🔀 Transfert inter-files du ticket:', {
        ticketId: this.currentTicket._id,
        currentQueue: this.currentTicket.queueId?.name,
        targetQueue: targetQueue.name,
        agencyId: this.userData.agencyId
      });

      // Émettre l'événement de transfert inter-files
      this.socket.emit('transfer_ticket_to_queue', {
        ticketId: this.currentTicket._id,
        targetQueueId: this.selectedTransferQueue,
        agencyId: this.userData.agencyId
      });

      // Écouter la confirmation
      this.socket.once('ticket_transferred_to_queue', (data) => {
        console.log('✅ Confirmation transfert inter-files:', data);
        toast.success(`Ticket transféré de ${this.currentTicket.number} vers file ${targetQueue.name} - Nouveau numéro: ${data.newNumber}`);
        this.showTransferModal = false;
        this.selectedTransferQueue = null;
        this.fetchQueue(); // Rafraîchir la file
      });

      // Écouter les erreurs
      this.socket.once('error', (error) => {
        console.error('❌ Erreur transfert inter-files:', error);
        toast.error(error.message || 'Erreur lors du transfert');
      });
    },

    // ✅ 8. Action : FIN DE SERVICE (terminer le ticket en cours)
    finishService() {
      if (!this.currentTicket) {
        toast.warning('Aucun ticket en cours');
        return;
      }

      console.log('✅ Fin de service pour ticket:', {
        ticketId: this.currentTicket._id,
        number: this.currentTicket.number,
        counterId: this.userData.assignedCounterId,
        agencyId: this.userData.agencyId
      });

      this.socket.emit('finish_ticket', {
        ticketId: this.currentTicket._id,
        counterId: this.userData.assignedCounterId,
        agencyId: this.userData.agencyId
      });

      toast.success(`Service terminé pour le ticket ${this.currentTicket.number}`);

      // Recharger la queue et l'historique après un court délai
      setTimeout(() => {
        console.log('🔄 Rechargement de la queue et historique après finish_ticket...');
        this.fetchQueue();
        this.fetchHistory(); // ✅ Recharger l'historique
      }, 500);
    },

    // ✅ 9. Charger la liste des guichets disponibles
    async fetchCounters() {
      try {
        const token = localStorage.getItem("userToken");
        const response = await fetch(`https://api.datafuse.fr/api/agency/counters`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          this.availableCounters = await response.json();
          // Filtrer pour ne pas afficher le guichet actuel
          this.availableCounters = this.availableCounters.filter(c => c._id !== this.userData.assignedCounterId);
        }
      } catch (err) {
        console.error('Erreur chargement guichets:', err);
      }
    },

    formatTime(dateStr) {
      if (!dateStr) return '--:--';
      const date = new Date(dateStr);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    getWaitTime(createdAt) {
      const start = new Date(createdAt);
      const now = new Date();
      return Math.floor((now - start) / 60000);
    },

    // Sélectionner une file en mode temporaire
    selectQueue(queueId) {
      this.selectedQueueId = queueId;
      localStorage.setItem("selectedQueueId", queueId);
      this.showQueueSelector = false;
      console.log(`✅ File sélectionnée: ${queueId}`);
      this.fetchQueue(); // Recharger les tickets de cette file
      toast.success(`File "${this.currentQueueName}" sélectionnée`);
    },

    // Réinitialiser et revenir au mode assigné
    resetToAssignedMode() {
      this.selectedQueueId = null;
      localStorage.removeItem("selectedQueueId");
      this.showQueueSelector = false;
      console.log(`🔄 Retour au mode assigné`);
      this.fetchQueue();
      toast.info("Retour au guichet assigné");
    },

    // Vérifier si on doit suggérer un switch automatique
    checkAutoSwitch() {
      // Ne pas suggérer si:
      // - Il y a encore des tickets en attente
      // - Il y a un ticket en cours (CALLED)
      // - Le modal est déjà affiché
      if (this.waitingTickets.length > 0 || this.currentTicket || this.showSwitchSuggestion) {
        return;
      }

      // Recharger les stats des files pour avoir les infos à jour
      this.fetchAvailableQueues().then(() => {
        // Trouver une file avec des tickets en attente (exclure la file actuelle)
        const currentQueueId = this.selectedQueueId;
        const queuesWithTickets = this.availableQueues
          .filter(q => q._id !== currentQueueId && q.waitingCount > 0)
          .sort((a, b) => b.waitingCount - a.waitingCount); // Trier par nombre décroissant

        if (queuesWithTickets.length > 0) {
          // Suggérer la file avec le plus de tickets
          this.suggestedQueue = queuesWithTickets[0];
          this.showSwitchSuggestion = true;
          console.log(`💡 Suggestion de switch vers ${this.suggestedQueue.name} (${this.suggestedQueue.waitingCount} tickets)`);
        }
      });
    },

    // Accepter le switch suggéré
    acceptSwitch() {
      if (this.suggestedQueue) {
        this.selectQueue(this.suggestedQueue._id);
        this.showSwitchSuggestion = false;
        this.suggestedQueue = null;
      }
    },

    // Refuser le switch suggéré
    declineSwitch() {
      this.showSwitchSuggestion = false;
      this.suggestedQueue = null;
      toast.info("Vous pouvez sélectionner manuellement une file");
    },

    // Rappeler un ticket depuis l'historique
    recallFromHistory(ticket) {
      if (!ticket) {
        console.error('❌ Ticket manquant');
        return;
      }

      if (!this.socketConnected) {
        toast.error('WebSocket non connecté');
        return;
      }

      console.log('🔄 Rappel depuis historique:', {
        ticketId: ticket._id,
        number: ticket.number,
        counterId: this.userData.assignedCounterId,
        counterName: this.counterName || this.userData.firstName
      });

      this.socket.emit('recall_ticket', {
        ticketId: ticket._id,
        counterId: this.userData.assignedCounterId,
        counterName: this.counterName || this.userData.firstName
      });

      toast.success(`📞 Ticket ${ticket.number} rappelé depuis l'historique`);
    }
  },
  mounted() {
    this.fetchAvailableQueues(); // Charger les files disponibles
    this.fetchQueue(); // Charger la queue actuelle
    this.fetchHistory(); // ✅ Charger l'historique des tickets terminés
    this.fetchCounters(); // ✅ Charger les guichets disponibles
    this.initSocket();

    // ✅ Timer pour mettre à jour le countdown chaque seconde
    this.countdownTimer = setInterval(() => {
      this.currentTime = Date.now();
    }, 1000);

    // Rafraîchir les statistiques des files toutes les 30 secondes
    this.statsRefreshInterval = setInterval(() => {
      this.fetchAvailableQueues();
      this.fetchHistory(); // ✅ Rafraîchir aussi l'historique périodiquement
    }, 30000);
  },
  beforeUnmount() {
    if (this.socket) this.socket.disconnect();
    if (this.statsRefreshInterval) clearInterval(this.statsRefreshInterval);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }
}
</script>